import { defineStore } from 'pinia';
import { login as loginApi } from '@/api/auth/login';
import { logout } from '@/api/auth/logout';
import { getAdminMe } from '@/api/auth/me';
import { getPermissionCodeFromRecord } from '@/constants/permissionRules';

export const useAdminInfo = defineStore('adminInfo', {
    state: () => ({
        id: '',
        name: '',
        email: '',
        roles: [],
        permissions: [],
        accessToken: '',
        tokenType: 'Bearer',
        expiresAt: '',
        isLoaded: false,
        /** 本 session 是否已向 /me 拉過最新權限（避免 localStorage 舊名單卡住） */
        profileFetched: false
    }),

    actions: {
        // 登入
        async login(email, password) {
            try {
                const response = await loginApi(email, password);

                if (response.result) {
                    return this.applyAccessToken(response.data);
                }

                return { success: false, message: response.message };
            } catch (error) {
                console.error('Login error:', error);
                return { success: false, message: error.message };
            }
        },

        /**
         * 寫入 token 並拉 /me（密碼登入與 SSO 共用）
         * @param {{ access_token: string, token_type?: string, expires_at?: string }} data
         */
        async applyAccessToken(data) {
            try {
                const access_token = data?.access_token;
                if (!access_token) {
                    return { success: false, message: '缺少 access_token' };
                }
                const token_type = data.token_type || 'Bearer';
                const expires_at = data.expires_at || '';

                this.accessToken = access_token;
                this.tokenType = token_type;
                this.expiresAt = expires_at;

                localStorage.setItem('accessToken', access_token);
                localStorage.setItem('tokenType', token_type);
                if (expires_at) {
                    localStorage.setItem('expiresAt', expires_at);
                }

                const profileOk =
                    (await this.fetchUserInfo(access_token)) ||
                    (await this.fetchUserInfo(access_token));
                if (!profileOk) {
                    console.warn(
                        '[login] /me 尚未成功，仍以 token 進入；路由守衛會重試'
                    );
                }

                return { success: true };
            } catch (error) {
                console.error('applyAccessToken error:', error);
                return { success: false, message: error.message };
            }
        },

        // 登出
        async logout() {
            try {
                await logout();
            } catch (error) {
                console.error('Logout error:', error);
            } finally {
                // 無論成功與否都清除本地資料
                this.clearAllInfo();
            }
        },

        // 獲取使用者資訊
        async fetchUserInfo(accessToken = null) {
            try {
                const token =
                    accessToken ||
                    this.accessToken ||
                    localStorage.getItem('accessToken');
                const response = await getAdminMe(token);

                if (response?.result) {
                    const { id, name, email, roles, all_permissions } =
                        response.data || {};

                    this.id = id;
                    this.name = name;
                    this.email = email;
                    this.roles = roles || [];
                    this.permissions = all_permissions || [];
                    this.isLoaded = true;
                    this.profileFetched = true;

                    // 儲存到 localStorage
                    this.saveToStorage();

                    return true;
                }

                const preview =
                    typeof response === 'string'
                        ? response.slice(0, 120)
                        : response;
                console.error(
                    'fetchUserInfo: unexpected response（若是 HTML，代表 /api 沒打到 Laravel）',
                    preview
                );
                return false;
            } catch (error) {
                console.error('Fetch user info error:', error);
                return false;
            }
        },

        // 獲取管理員信息
        getAdminInfo() {
            // 如果 store 中已經有數據，直接返回
            if (this.isLoaded) {
                return {
                    id: this.id,
                    name: this.name,
                    email: this.email,
                    role: this.roles.length > 0 ? this.roles[0].name : '',
                    roles: this.roles,
                    permissions: this.permissions
                };
            }

            // store 中沒有數據時，才從 localStorage 加載
            this.loadFromStorage();
            return {
                id: this.id,
                name: this.name,
                email: this.email,
                role: this.roles.length > 0 ? this.roles[0].name : '',
                roles: this.roles,
                permissions: this.permissions
            };
        },

        // 設置管理員資訊 (保留向後相容性)
        setAdminInfo(adminData) {
            if (adminData.id !== undefined) this.id = adminData.id;
            if (adminData.name !== undefined) this.name = adminData.name;
            if (adminData.email !== undefined) this.email = adminData.email;
            if (adminData.roles !== undefined) this.roles = adminData.roles;
            if (adminData.permissions !== undefined)
                this.permissions = adminData.permissions;

            // 舊版相容性處理
            if (adminData.role && !adminData.roles) {
                this.roles = [{ name: adminData.role }];
            }

            this.isLoaded = true;
            this.saveToStorage();
        },

        // 儲存到 localStorage
        saveToStorage() {
            localStorage.setItem(
                'adminInfo',
                JSON.stringify({
                    id: this.id,
                    name: this.name,
                    email: this.email,
                    roles: this.roles,
                    permissions: this.permissions
                })
            );
        },

        // 從 localStorage 載入資訊（顯示用；session 內須再打 /me 才算正式就緒）
        loadFromStorage() {
            const storedInfo = localStorage.getItem('adminInfo');
            const accessToken = localStorage.getItem('accessToken');
            const tokenType = localStorage.getItem('tokenType');
            const expiresAt = localStorage.getItem('expiresAt');

            // /me 已打過則勿用 localStorage 舊權限覆蓋
            if (storedInfo && !this.profileFetched) {
                try {
                    const adminData = JSON.parse(storedInfo);
                    this.id = adminData.id || '';
                    this.name = adminData.name || '';
                    this.email = adminData.email || '';
                    this.roles = adminData.roles || [];
                    this.permissions = adminData.permissions || [];

                    // 舊版相容性處理
                    if (adminData.role && !adminData.roles) {
                        this.roles = [{ name: adminData.role }];
                    }
                } catch (error) {
                    console.error('Error parsing stored admin info:', error);
                }
            }

            if (accessToken) {
                this.accessToken = accessToken;
                this.tokenType = tokenType || 'Bearer';
                this.expiresAt = expiresAt || '';
            }
        },

        // 清除所有資訊（登出時使用）
        clearAllInfo() {
            this.id = '';
            this.name = '';
            this.email = '';
            this.roles = [];
            this.permissions = [];
            this.accessToken = '';
            this.tokenType = 'Bearer';
            this.expiresAt = '';
            this.isLoaded = false;
            this.profileFetched = false;

            // 清除 localStorage
            localStorage.removeItem('adminInfo');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('tokenType');
            localStorage.removeItem('expiresAt');
            localStorage.removeItem('lastLoginTime');
            localStorage.removeItem('isFakeLogin');
        },

        // 清除資訊（保留向後相容性）
        clearAdminInfo() {
            this.clearAllInfo();
        },

        // 檢查權限
        hasPermission(permissionName) {
            return this.permissions.some((p) => p.name === permissionName);
        },

        // 檢查是否有任一權限
        hasAnyPermission(permissionNames) {
            return permissionNames.some((name) => this.hasPermission(name));
        },

        // 檢查是否有所有權限
        hasAllPermissions(permissionNames) {
            return permissionNames.every((name) => this.hasPermission(name));
        },

        // 以代碼檢查權限（容錯常見鍵名：code / permission_code / permission）
        hasPermissionCode(permissionCode) {
            const target = String(permissionCode);
            return this.permissions.some(
                (p) => getPermissionCodeFromRecord(p) === target
            );
        },

        // 是否擁有任一代碼
        hasAnyPermissionCode(codes) {
            const targets = (codes || []).map((c) => String(c));
            return this.permissions.some((p) =>
                targets.includes(getPermissionCodeFromRecord(p))
            );
        }
    },

    getters: {
        // 檢查是否已載入資訊
        hasAdminInfo: (state) => state.isLoaded,

        // 檢查是否已登入
        isAuthenticated: (state) => !!state.accessToken && state.isLoaded,

        // 取得第一個角色名稱（向後相容）
        roleName: (state) => (state.roles.length > 0 ? state.roles[0].name : ''),

        /** Spatie admin 角色：後端 full sync，前端側欄／路由應全開 */
        isFullAccessAdmin: (state) =>
            (state.roles || []).some((r) => {
                const name = typeof r === 'string' ? r : r?.name;
                return String(name || '').toLowerCase() === 'admin';
            })
    }
});
